import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import api from '../api/api';

// Начальное состояние
const initialState = {
  user: null,
  tickets: [],
  wallets: [],
  transactions: [],
  loading: false,
  error: null
};

// Типы действий
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_USER: 'SET_USER',
  SET_TICKETS: 'SET_TICKETS',
  ADD_TICKET: 'ADD_TICKET',
  UPDATE_TICKET: 'UPDATE_TICKET',
  SET_WALLETS: 'SET_WALLETS',
  SET_TRANSACTIONS: 'SET_TRANSACTIONS',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_DATA: 'CLEAR_DATA'
};

// Редьюсер
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case ACTIONS.SET_USER:
      return { ...state, user: action.payload };
    case ACTIONS.SET_TICKETS:
      return { ...state, tickets: action.payload };
    case ACTIONS.ADD_TICKET:
      return { ...state, tickets: [action.payload, ...state.tickets] };
    case ACTIONS.UPDATE_TICKET:
      return {
        ...state,
        tickets: state.tickets.map(ticket =>
          ticket.id === action.payload.id ? action.payload : ticket
        )
      };
    case ACTIONS.SET_WALLETS:
      return { ...state, wallets: action.payload };
    case ACTIONS.SET_TRANSACTIONS:
      return { ...state, transactions: action.payload };
    case ACTIONS.UPDATE_USER:
      return { ...state, user: action.payload };
    case ACTIONS.CLEAR_DATA:
      return initialState;
    default:
      return state;
  }
};

// Кэш для API запросов
const cache = new Map();
const cacheTimeout = 5 * 60 * 1000; // 5 минут

// Создаем контекст
const AppContext = createContext();

// Провайдер контекста
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const abortControllersRef = useRef(new Map());

  // Общая функция для API запросов без кэширования
  const makeApiRequest = useCallback(async (endpoint, options = {}) => {
    // Отменяем предыдущий запрос для этого эндпоинта
    const existingController = abortControllersRef.current.get(endpoint);
    if (existingController) {
      existingController.abort();
    }

    // Создаем новый контроллер для этого эндпоинта
    const abortController = new AbortController();
    abortControllersRef.current.set(endpoint, abortController);

    try {
      const token = sessionStorage.getItem("accessToken");
      const response = await api.get(endpoint, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        signal: abortController.signal,
      });

      const data = response.data;
      
      // Удаляем контроллер после успешного запроса
      abortControllersRef.current.delete(endpoint);

      return data;
    } catch (error) {
      // Удаляем контроллер при ошибке
      abortControllersRef.current.delete(endpoint);
      
      if (error.name !== 'AbortError') {
        throw error;
      }
    }
  }, []);

  // Функция для получения тикетов
  const fetchTickets = useCallback(async () => {
    // Проверяем, есть ли уже активный запрос
    if (abortControllersRef.current.has('/tickets')) {
      return; // Не делаем новый запрос, если уже есть активный
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
      const data = await makeApiRequest('/tickets');
      dispatch({ type: ACTIONS.SET_TICKETS, payload: data });
      return data;
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, [makeApiRequest]);

  // Функция для создания тикета
  const createTicket = useCallback(async (ticketData) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
      
      const token = sessionStorage.getItem("accessToken");
      const response = await api.post('/tickets', ticketData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      const data = response.data;
      
      dispatch({ type: ACTIONS.ADD_TICKET, payload: data });
      return data;
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, []);

  // Функция для получения кошельков
  const fetchWallets = useCallback(async () => {
    // Проверяем, есть ли уже активный запрос
    if (abortControllersRef.current.has('/wallets')) {
      return; // Не делаем новый запрос, если уже есть активный
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
      const data = await makeApiRequest('/wallets');
      dispatch({ type: ACTIONS.SET_WALLETS, payload: data });
      return data;
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, [makeApiRequest]);

  // Функция для получения транзакций
  const fetchTransactions = useCallback(async () => {
    // Проверяем, есть ли уже активный запрос
    if (abortControllersRef.current.has('/transactions')) {
      return; // Не делаем новый запрос, если уже есть активный
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
      const data = await makeApiRequest('/transactions');
      dispatch({ type: ACTIONS.SET_TRANSACTIONS, payload: data });
      return data;
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, [makeApiRequest]);

  // Функция для получения данных пользователя
  const fetchUser = useCallback(async () => {
    // Проверяем, есть ли уже активный запрос
    if (abortControllersRef.current.has('/auth/user')) {
      return; // Не делаем новый запрос, если уже есть активный
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
      const data = await makeApiRequest('/auth/user');
      dispatch({ type: ACTIONS.UPDATE_USER, payload: data });
      return data;
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, [makeApiRequest]);

  // Функция для обновления профиля пользователя
  const updateUser = useCallback(async (userData) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
      
      const token = sessionStorage.getItem("accessToken");
      const response = await api.put('/auth/profile', userData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      const data = response.data;
      
      dispatch({ type: ACTIONS.UPDATE_USER, payload: data });
      return data;
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, []);

  // Очистка данных при выходе
  const clearData = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_DATA });
    // Отменяем все активные запросы
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();
  }, []);

  const value = {
    ...state,
    fetchTickets,
    createTicket,
    fetchWallets,
    fetchTransactions,
    fetchUser,
    updateUser,
    clearData
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Хук для использования контекста
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}; 